import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {breakpoint, m, mr, mb, py, pr, pl} from '@nib/layout';
import {TickSystemIcon} from '@nib/icons';
import {
  copyFontFamily,
  copyColor,
  formInputBackgroundColor,
  formBorderColor,
  formSelectedToggleColor,
  colorWhite,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset
} from '@nib-components/theme';

const disabledCursor = 'not-allowed';
const disabledOpacity = 0.5;

const validFormModes = ['light', 'white'] as const;
type FormModes = (typeof validFormModes)[number];

const validToggleTypes = ['checkbox', 'radio'] as const;
type ToggleTypes = (typeof validToggleTypes)[number];

export interface ToggleInputProps {
  type?: ToggleTypes;
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  subtitle?: string;
  boldLabel?: boolean;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  formMode?: FormModes;
  inline?: boolean;
  isCompact?: boolean;
  hideInput?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // ...otherProps
}

export interface WrapperProps {
  isCompact?: boolean;
  inline?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // ...otherProps
}

export interface LabelProps {
  type?: ToggleTypes;
  boldLabel?: boolean;
  hideInput?: boolean;
  isCompact?: boolean;
  selected?: boolean;
  disabled?: boolean;
  formMode?: FormModes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // ...otherProps
}

export interface InputProps {
  valid?: boolean;
  validated?: boolean;
  isCompact?: boolean;
  isEmptyAndOptional?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // ...otherProps
}

export interface StyledTickProps {
  isCompact?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // ...otherProps
}

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const Wrapper = styled(({isCompact, inline, ...rest}) => <div {...rest} />)<WrapperProps>`
  position: relative;

  ${breakpoint('sm')`    
    ${({inline}: {inline: boolean}) => inline && `display: inline-block;`};

    ${({isCompact, inline}: {isCompact: boolean; inline: boolean}) =>
      inline &&
      isCompact &&
      css`
        &:not(:last-child) {
          ${mr(2)}
        }
      `}
    ${({isCompact, inline}: {isCompact: boolean; inline: boolean}) =>
      inline &&
      !isCompact &&
      css`
        &:not(:last-child) {
          ${mr(4)}
        }
      `}
  `}
`;
Wrapper.displayName = 'ToggleInputWrapper';

/* eslint-disable */
const Label = styled(({type, hideInput, isCompact, boldLabel, formMode, ...rest}) => <label {...rest} />)<LabelProps>`
  ${py(4)};
  ${pr(6)};

  ${({hideInput}: {hideInput: boolean}) => (hideInput ? pl(6) : pl(8))};

  /* IMPORTANT! If you modify this margin bottom, please also update the negative margin in InputWrapper in FormControl */
  ${({isCompact}: {isCompact: boolean}) =>
    isCompact
      ? css`
          ${mb(2)};
        `
      : css`
          ${mb(4)};
        `};

  display: block;
  position: relative;
  text-align: left;

  ${breakpoint('sm')`
    display: inline-block;
  `}

  background-color: ${formInputBackgroundColor};
  font-family: ${copyFontFamily};
  font-weight: ${({boldLabel}: {boldLabel?: boolean}) => (boldLabel ? 700 : 400)};
  line-height: 1.5;
  color: ${copyColor};
  cursor: pointer;
  transition: background-color 0.2s;

  ${({disabled}: {disabled: boolean}) =>
    disabled &&
    css`
      opacity: ${disabledOpacity};
      cursor: ${disabledCursor};
    `};

  ${({hideInput}: {hideInput: boolean}) =>
    !hideInput &&
    css`
      &::before,
      &::after {
        content: '';
        display: block;
        position: absolute;
        box-sizing: border-box;
        background-color: ${colorWhite};
        top: 50%;
        transform: translateY(-50%);
      }
    `}

  ${({isCompact}: {isCompact: boolean}) =>
    isCompact
      ? css`
          ${py(2)};
          ${pr(4)};
          ${pl(7)};
          font-size: 0.875rem;

          &::before {
            width: 1.5rem;
            height: 1.5rem;
            left: 0.75rem;
            box-shadow: inset 0 0 0 2px ${formBorderColor};
          }

          &::after {
            width: 0.75rem;
            height: 0.75rem;
            left: 1.125rem;
          }
        `
      : css`
          &::before {
            width: 2rem;
            height: 2rem;
            left: 1rem;
            box-shadow: inset 0 0 0 2px ${formBorderColor};
          }

          &::after {
            width: 1rem;
            height: 1rem;
            left: 1.5rem;
          }
        `};

  ${({selected}: {selected: boolean}) =>
    selected &&
    css`
      box-shadow: inset 0 0 0 2px ${formSelectedToggleColor};
    `};

  ${({selected, hideInput}: {selected: boolean; hideInput: boolean}) =>
    selected &&
    hideInput &&
    css`
      color: ${formSelectedToggleColor};
      background-color: ${colorWhite};
    `};

  ${({selected, hideInput}: {selected: boolean; hideInput: boolean}) =>
    selected &&
    !hideInput &&
    css`
      &::before {
        background-color: ${colorWhite};
        box-shadow: inset 0 0 0 2px ${formSelectedToggleColor};
      }
      &::after {
        background-color: ${formSelectedToggleColor};
      }
    `};

  /* Type prop based */
  ${({type}: {type: ToggleTypes}) =>
    css`
      &::before,
      &::after {
        border-radius: ${type === 'radio' ? '50%' : '2px'};
      }
    `};

  /* Checkbox */
  ${({type}: {type: ToggleTypes}) =>
    type === 'checkbox' &&
    `
      &::after {
        content: none;
      }
    `};

  /* Selected checkbox will display the <TickIcon> in the <ToggleInput> render */
`;
/* eslint-enable */

Label.displayName = 'ToggleInputLabel';

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const Input = styled(({valid, validated, isCompact, isEmptyAndOptional, ...rest}) => <input {...rest} />)<InputProps>`
  ${m(0)};
  position: absolute;
  top: calc(50% - 8px);
  transform: translateY(-50%);

  /* Hard coded left value so that the positioning of the input works across brands */
  left: 1.5rem;
  cursor: pointer;

  /* a11y hidden input */
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;

  ${({isCompact}: {isCompact: boolean}) =>
    isCompact &&
    `
    top: calc(50% - 4px);
    left: 1rem;
  `};

  &:focus + ${Label} {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
    position: relative;
    z-index: 1;
  }
`;

Input.displayName = 'ToggleInputInput';

const Subtitle = styled.span`
  display: block;
  font-weight: 400;
`;

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const StyledTick = styled(({isCompact, ...rest}) => <TickSystemIcon {...rest} />)<StyledTickProps>`
  position: absolute;
  box-sizing: border-box;
  background-color: transparent;
  color: ${formSelectedToggleColor};
  top: 50%;
  transform: translateY(-50%);

  /* IE compatibility (doesn't render correctly without a transparent background-color and no height) */
  @supports (height: auto) {
    height: auto;
    background-color: ${colorWhite};
  }

  ${({isCompact}: {isCompact: boolean}) =>
    isCompact
      ? `
      width: 1rem;
      left: 1rem;
  `
      : `
      width: 1.25rem;
      left: 1.375rem;
  `};
`;

const ToggleInput: React.FC<ToggleInputProps> & {Input: typeof Input} & {
  Label: typeof Label;
} & {Wrapper: typeof Wrapper} = (props): JSX.Element => {
  const {type, id, label, subtitle, selected, disabled, className, formMode, inline, isCompact, hideInput, boldLabel, ...otherProps} = props;
  return (
    <Wrapper inline={inline} isCompact={isCompact}>
      {/* Dummy onChange function is here to suppress the react warning of setting checked without it, can be overridden */}
      {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
      <Input onChange={() => {}} {...otherProps} id={id} type={type} checked={selected} disabled={disabled} isCompact={isCompact} />
      <Label htmlFor={id} disabled={disabled} selected={selected} type={type} className={className} formMode={formMode} isCompact={isCompact} hideInput={hideInput} boldLabel={boldLabel || !!subtitle}>
        {type === 'checkbox' && selected && !hideInput && <StyledTick isCompact={isCompact} />}
        {label}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Label>
    </Wrapper>
  );
};

ToggleInput.displayName = 'ToggleInput';

ToggleInput.Input = Input;
ToggleInput.Label = Label;
ToggleInput.Wrapper = Wrapper;

ToggleInput.propTypes = {
  type: PropTypes.oneOf(validToggleTypes).isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  boldLabel: PropTypes.bool,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  formMode: PropTypes.oneOf(validFormModes),
  inline: PropTypes.bool,
  isCompact: PropTypes.bool,
  hideInput: PropTypes.bool
};

ToggleInput.defaultProps = {
  inline: true,
  hideInput: false
};

export default ToggleInput;
